import { NgClass } from "@angular/common";
import { Component, ElementRef, forwardRef, signal, ViewChild } from "@angular/core";
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from "@angular/forms";
import { MatButton } from "@angular/material/button";
import { MatError } from "@angular/material/form-field";
import { MatIcon } from "@angular/material/icon";
import { IProductMediaListRequestDto, IProductMediaRequestDto } from "@models/product-list.model";
import { ImageUrlDirective } from "../../directives/image-url.directive";

export interface IMediaPicker {
  images: IProductMediaListRequestDto;
  videos: IProductMediaListRequestDto;
}

/** Plain FileReader read, as a promise — the original (pre-downscale) behavior. */
function readAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

/**
 * A phone camera photo is routinely 3-10MB — sent as base64 (a ~33% size increase)
 * embedded straight in the product-save JSON body, a handful of them can push a
 * mobile upload well past what the network/host stack tolerates, surfacing as an
 * opaque failure with no useful message. Redrawing onto a canvas produces a real
 * JPEG (fixing an iPhone HEIC capture too, which browsers can't otherwise display)
 * at a size that comfortably fits any reasonable request-body limit.
 */
const MAX_IMAGE_DIMENSION = 1600;
const IMAGE_QUALITY = 0.82;

function downscaleImageToJpeg(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const objectUrl = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(objectUrl);
      const scale = Math.min(1, MAX_IMAGE_DIMENSION / Math.max(img.width, img.height));
      const canvas = document.createElement("canvas");
      canvas.width = Math.round(img.width * scale);
      canvas.height = Math.round(img.height * scale);
      const ctx = canvas.getContext("2d");
      if (!ctx) {
        reject(new Error("2D canvas context unavailable"));
        return;
      }
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      resolve(canvas.toDataURL("image/jpeg", IMAGE_QUALITY));
    };
    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error("Could not decode image"));
    };
    img.src = objectUrl;
  });
}

@Component({
  selector: "fh-media-picker",
  templateUrl: "./media-picker.component.html",
  styleUrls: ["./media-picker.component.scss"],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => MediaPickerComponent),
      multi: true,
    },
  ],
  standalone: true,
  imports: [MatError, MatIcon, MatButton, NgClass, ImageUrlDirective],
})
export class MediaPickerComponent implements ControlValueAccessor {
  @ViewChild("multifile") multifile!: ElementRef<HTMLInputElement>;
  @ViewChild("multivdo") multivdo!: ElementRef<HTMLInputElement>;

  images = signal<IProductMediaListRequestDto>([]);
  videos = signal<IProductMediaListRequestDto>([]);

  deletedImages = signal<IProductMediaListRequestDto>([]);
  deletedVideos = signal<IProductMediaListRequestDto>([]);

  imageErrorMsg = "";
  videoErrorMsg = "";

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  onChange: (value: any) => void = () => {};
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  onTouched: () => void = () => {};

  writeValue(obj: IMediaPicker | null): void {
    if (obj) {
      this.images.set(obj.images || []);
      this.videos.set(obj.videos || []);
    }
  }

  registerOnChange(fn: (value: any) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  onImageSelection(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = "";
    if (!file) return;

    downscaleImageToJpeg(file)
      .catch(() => readAsDataUrl(file)) // e.g. a format the browser can't decode into an <img> — send the original rather than fail the whole upload
      .then((result) => {
        this.images.update((prev) => [...prev, { base64: result, state: "add" }]);
        this.updateFormValue();
      });
  }

  onVideoSelection(event: Event): void {
    this.handleFileSelection(event, "video", (result, _ext, file) => {
      this.videos.update((prev) => [...prev, { base64: result, state: "add", file }]);
      this.updateFormValue();
    });
  }

  handleFileSelection(
    event: Event,
    _type: "image" | "video",
    callback: (result: string, ext: string, file?: File) => void
  ): void {
    const input = event.target as HTMLInputElement;
    if (input.files) {
      const file = input.files[0];
      const reader = new FileReader();
      const ext = file.name.split(".").pop()?.toLowerCase() || "";

      reader.onload = () => {
        callback(reader.result as string, ext, file);
      };
      reader.readAsDataURL(file);

      input.value = "";
    }
  }

  deleteMedia(index: number, mediaType: "image" | "video"): void {
    const mediaList = mediaType === "image" ? this.images() : this.videos();
    const updateMedia =
      mediaType === "image"
        ? (items: IProductMediaListRequestDto) => this.images.set(items)
        : (items: IProductMediaListRequestDto) => this.videos.set(items);

    if (mediaList[index].state === "add") {
      // If the item is newly added, remove it from the list
      const updatedMediaList = mediaList.filter((_, i) => i !== index);
      updateMedia(updatedMediaList as IProductMediaListRequestDto);
    } else {
      // Otherwise, mark the item as deleted
      const updatedMediaList = mediaList.map((item, i) =>
        i === index ? ({ ...item, state: "delete" } as IProductMediaRequestDto) : item
      );

      const { mediaToShow, mediaToDelete } = updatedMediaList.reduce(
        (
          acc: {
            mediaToShow: IProductMediaListRequestDto;
            mediaToDelete: IProductMediaListRequestDto;
          },
          item
        ) => {
          if (item.state === "delete") {
            acc.mediaToDelete.push(item);
          } else {
            acc.mediaToShow.push(item);
          }
          return acc;
        },
        { mediaToShow: [], mediaToDelete: [] }
      );

      // Show only the non-deleted items (was passing updatedMediaList, which still contained
      // the delete-marked item, so deleted media reappeared in the picker).
      updateMedia(mediaToShow as IProductMediaListRequestDto);
      // Also update the original list to include this deleted item
      if (mediaType === "image") {
        this.images.set(mediaToShow);
        this.deletedImages.update((prev) => [...prev, ...mediaToDelete]);
      } else {
        this.videos.set(mediaToShow);
        this.deletedVideos.update((prev) => [...prev, ...mediaToDelete]);
      }
    }

    this.updateFormValue();
  }

  // Wrapper methods for images and videos
  deleteImage(index: number): void {
    this.deleteMedia(index, "image");
  }

  deleteVideo(index: number): void {
    this.deleteMedia(index, "video");
  }

  markAsFeatured(index: number): void {
    // Unmark any currently featured image
    this.images.update((prev) =>
      prev.map((item, i) => ({
        ...item,
        is_featured: i === index,
      }))
    );
    this.updateFormValue();
  }

  removeFeatured(index: number): void {
    // Unmark the featured image
    this.images.update((prev) =>
      prev.map((item, i) => ({
        ...item,
        is_featured: i === index ? false : item.is_featured,
      }))
    );
    this.updateFormValue();
  }

  emptyBoxes(array: IProductMediaListRequestDto, fileType: "video" | "image"): number[] {
    const maxSize = fileType === "video" ? 8 : 36;
    const activeItems = array.filter((item) => item.state !== "delete").length;

    // Return array with single element if we haven't reached minSize
    if (activeItems < maxSize) {
      return [0];
    }

    // Return empty array if we've reached or exceeded minSize
    return [];
  }

  updateFormValue(): void {
    this.onChange({
      images: [...this.images(), ...this.deletedImages()],
      videos: [...this.videos(), ...this.deletedVideos()],
    });
  }
}
